/**
 * Sidebar com ícones e tooltips
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  Divider
} from '@mui/material';
import {
  DashboardOutlined,
  SearchOutlined,
  AssignmentOutlined,
  PeopleOutlined,
  SettingsOutlined,
  BusinessCenterOutlined,
  AdminPanelSettingsOutlined
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface MenuItem {
  id: string;
  icon: React.ReactElement;
  label: string;
  path: string;
  adminOnly?: boolean;
}

const menuItems: MenuItem[] = [
  {
    id: 'dashboard',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
    path: '/app'
  },
  {
    id: 'search',
    icon: <SearchOutlined />,
    label: 'Buscar Estabelecimentos',
    path: '/app/search'
  },
  {
    id: 'tasks',
    icon: <AssignmentOutlined />,
    label: 'Tarefas',
    path: '/app/tasks'
  },
  {
    id: 'leads',
    icon: <PeopleOutlined />,
    label: 'Gestão de Leads',
    path: '/app/leads'
  },
  {
    id: 'users',
    icon: <AdminPanelSettingsOutlined />,
    label: 'Gerenciar Usuários',
    path: '/app/users',
    adminOnly: true
  }
];

const bottomMenuItems: MenuItem[] = [
  {
    id: 'settings',
    icon: <SettingsOutlined />,
    label: 'Configurações',
    path: '/app/settings'
  }
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();

  const isActive = (path: string) => {
    if (path === '/app') {
      return location.pathname === '/app';
    }
    return location.pathname.startsWith(path);
  };

  // Filtrar items baseado em permissões
  const filteredMenuItems = menuItems.filter(item => !item.adminOnly || isAdmin);

  return (
    <Box
      sx={{
        width: 70,
        height: '100vh',
        backgroundColor: '#000000',
        borderRight: '1px solid rgba(255, 107, 53, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1200,
      }}
    >
      {/* Logo Icon */}
      <Box
        sx={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255, 107, 53, 0.1)',
        }}
      >
        <BusinessCenterOutlined
          sx={{
            color: '#FF6B35',
            fontSize: 32,
          }}
        />
      </Box>

      {/* Main Menu Items */}
      <List sx={{ flex: 1, py: 2 }}>
        {filteredMenuItems.map((item) => (
          <Tooltip
            key={item.id}
            title={item.label}
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: '#1a1a1a',
                  color: '#FFFFFF',
                  fontSize: '0.875rem',
                  border: '1px solid rgba(255, 107, 53, 0.3)',
                  '& .MuiTooltip-arrow': {
                    color: '#1a1a1a',
                    '&::before': {
                      border: '1px solid rgba(255, 107, 53, 0.3)',
                    }
                  }
                }
              }
            }}
          >
            <ListItemButton
              onClick={() => navigate(item.path)}
              sx={{
                py: 2,
                justifyContent: 'center',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  backgroundColor: '#FF6B35',
                  opacity: isActive(item.path) ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                },
                backgroundColor: isActive(item.path) ? 'rgba(255, 107, 53, 0.1)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(255, 107, 53, 0.15)',
                  '&::before': {
                    opacity: 0.5,
                  }
                }
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 'auto',
                  color: isActive(item.path) ? '#FF6B35' : 'rgba(255, 255, 255, 0.7)',
                  transition: 'color 0.3s ease',
                }}
              >
                {item.icon}
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ))}
      </List>

      <Divider sx={{ borderColor: 'rgba(255, 107, 53, 0.1)' }} />

      {/* Bottom Menu Items */}
      <List sx={{ pb: 2 }}>
        {bottomMenuItems.map((item) => (
          <Tooltip
            key={item.id}
            title={item.label}
            placement="right"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: '#1a1a1a',
                  color: '#FFFFFF',
                  fontSize: '0.875rem',
                  border: '1px solid rgba(255, 107, 53, 0.3)',
                  '& .MuiTooltip-arrow': {
                    color: '#1a1a1a',
                  }
                }
              }
            }}
          >
            <ListItemButton
              onClick={() => navigate(item.path)}
              sx={{
                py: 2,
                justifyContent: 'center',
                backgroundColor: isActive(item.path) ? 'rgba(255, 107, 53, 0.1)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(255, 107, 53, 0.15)',
                }
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 'auto',
                  color: isActive(item.path) ? '#FF6B35' : 'rgba(255, 255, 255, 0.7)',
                }}
              >
                {item.icon}
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
    </Box>
  );
};

export default Sidebar;
